"use client";

import React, { useEffect, useRef, useState } from "react";
import { Cpu, Users, Wrench, Terminal } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const teamData = [{
  id: "APEX-01",
  name: "Marcus Chen",
  role: "Lead Architect",
  subRole: "Software Engineering Specialist",
  bio: "Pioneering custom software solutions with a focus on scalable architecture and high-performance computing.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687829/img/marcus_architect_portrait.png",
  icon: Terminal,
  skills: [{
    name: "System Architecture",
    value: 98
  }, {
    name: "Cloud Infrastructure",
    value: 92
  }, {
    name: "AI Integration",
    value: 88
  }]
}, {
  id: "APEX-02",
  name: "Sarah Jenkins",
  role: "Cybersecurity Lead",
  subRole: "Security Operations Expert",
  bio: "Dedicated to fortifying digital assets through advanced threat detection and proactive defense strategies.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687829/img/sarah_security_portrait.png",
  icon: Cpu,
  skills: [{
    name: "Threat Analysis",
    value: 95
  }, {
    name: "Network Security",
    value: 94
  }, {
    name: "Compliance",
    value: 90
  }]
}, {
  id: "APEX-03",
  name: "David Miller",
  role: "Solutions Director",
  subRole: "IT Consulting Strategist",
  bio: "Bridging the gap between complex technology and business objectives through strategic IT consulting.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687829/img/david_solutions_portrait.png",
  icon: Users,
  skills: [{
    name: "IT Strategy",
    value: 96
  }, {
    name: "Project Management",
    value: 93
  }, {
    name: "Client Relations",
    value: 97
  }]
}];
export default function TheArchitectsOfInnovation() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  return <section data-section-id="236479" id="the-architects-of-innovation" className="bg-background text-foreground relative overflow-hidden py-24">
      {}
      <div className="pointer-events-none absolute inset-0 z-0 opacity-[0.05]">
        <svg className="h-full w-full" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
          <defs>
            <pattern id="blueprint-grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#blueprint-grid)" />
        </svg>
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div ref={headerRef} className={`mb-16 text-center transition-all duration-1000 ${isHeaderVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
          <div className="mb-4 flex items-center justify-center gap-2 text-primary">
            <Terminal className="size-5" />
            <span className="font-mono text-sm font-semibold uppercase tracking-widest">
              System Core Personnel
            </span>
          </div>
          <h2 className="font-serif mb-4 text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl">
            The Architects of Innovation
          </h2>
          <p className="mx-auto max-w-2xl text-muted-foreground font-default">
            A collective of elite engineers and strategists dedicated to engineering the future of IT solutions for Apex.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
          {teamData.map((member, i) => <TeamMemberCard key={member.id} member={member} index={i} />)}
        </div>
      </div>
    </section>;
}
function TeamMemberCard({
  member,
  index
}: {
  member: (typeof teamData)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const Icon = member.icon;
  return <div ref={ref} data-index={index} className={`transition-all duration-1000 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-16 opacity-0"}`} style={{
    transitionDelay: `${index * 150}ms`
  }}>
      <Card className="group relative h-full overflow-hidden border-border/20 bg-card/30 backdrop-blur-xl transition-all duration-500 hover:border-primary/40 hover:shadow-3xl">
        {}
        <div className="relative aspect-[4/5] w-full overflow-hidden bg-muted/10">
          <div className="absolute inset-0 z-10 bg-gradient-to-t from-background via-transparent to-transparent opacity-60" />
          <img src={member.image} alt={`Portrait of ${member.name}`} className="h-full w-full object-cover object-center grayscale transition-all duration-700 group-hover:scale-110 group-hover:grayscale-0" />
          
          {}
          <div className="absolute left-4 top-4 z-20 rounded-md bg-background/90 px-2 py-1 font-mono text-[10px] font-bold text-primary backdrop-blur-md border border-primary/20">
            ID::{member.id}
          </div>
        </div>

        <CardHeader className="relative z-20 -mt-16 pb-2">
          <div className="mb-2 flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-primary/30">
              <Icon className="size-5" />
            </div>
            <div>
              <h3 className="font-serif text-2xl font-bold text-foreground tracking-tight">
                {member.name}
              </h3>
              <div className="inline-block rounded-sm bg-primary px-2 py-0.5 mt-1">
                <p className="font-mono text-[10px] font-bold text-primary-foreground uppercase tracking-widest">
                  {member.role}
                </p>
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent className="relative z-20 flex flex-col gap-6 pt-4">
          <div className="space-y-2">
            <p className="font-mono text-[10px] text-muted-foreground">
              <span className="text-primary/80">STATUS:</span>{" "}
              {member.subRole}
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed font-default">
              {member.bio}
            </p>
          </div>

          {}
          <div className="mt-auto space-y-4 border-t border-border/10 pt-4">
            <p className="font-mono text-[10px] font-semibold text-foreground/60 uppercase tracking-widest">
              Technical Specifications
            </p>
            <div className="space-y-3">
              {member.skills.map((skill, skillIndex) => <div key={skillIndex} data-index={skillIndex} className="space-y-1.5">
                  <div className="flex justify-between font-mono text-[10px]">
                    <span className="text-muted-foreground">{skill.name}</span>
                    <span className="text-primary">{skill.value}%</span>
                  </div>
                  <Progress value={skill.value} className="h-1 bg-muted/30" />
                </div>)}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>;
}